import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../utils/api';
import { Plus, FileText, Trash2, Eye, Download, FileSpreadsheet, Filter, X, Scan } from 'lucide-react';
import { toast } from 'sonner';
import { handleNumericInputFocus, handleNumericInputMouseUp } from '../utils/inputHelpers';

export const DeliveryNotes = () => {
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Download filename dialog state
  const [filenameDialogOpen, setFilenameDialogOpen] = useState(false);
  const [downloadType, setDownloadType] = useState('pdf'); // 'pdf' or 'excel'
  const [noteToDownload, setNoteToDownload] = useState(null);
  const [customFilename, setCustomFilename] = useState('');
  const [downloading, setDownloading] = useState(false);
  
  // Filter state
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Form state
  const [type, setType] = useState('supplier');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [supplierId, setSupplierId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [eventId, setEventId] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  
  // Data
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const fetchDeliveryNotes = React.useCallback(async () => {
    try {
      const response = await api.getDeliveryNotes();
      setDeliveryNotes(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching delivery notes:', error);
    }
  }, []);

  const fetchProducts = React.useCallback(async () => {
    try {
      const response = await api.getProducts();
      setProducts(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching products:', error);
    }
  }, []);

  const fetchSuppliers = React.useCallback(async () => {
    try {
      const response = await api.getSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching suppliers:', error);
    }
  }, []);

  const fetchCustomers = React.useCallback(async () => {
    try {
      const response = await api.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching customers:', error);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchDeliveryNotes();
    fetchProducts();
    fetchSuppliers();
    fetchCustomers();
  }, [fetchDeliveryNotes, fetchProducts, fetchSuppliers, fetchCustomers]);

  // POLLING DESACTIVADO - causaba recargas visuales y pérdida de foco
  // useEffect(() => {
  //   const pollingInterval = setInterval(() => {
  //     fetchDeliveryNotes();
  //   }, 5000);
  //   return () => clearInterval(pollingInterval);
  // }, [fetchDeliveryNotes]);

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: 1, price: 0, is_box: false }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // Helper function to calculate price based on product and selection type
  const calculateItemPrice = (product, isBox, noteType) => {
    if (!product) return 0;
    
    const hasBox = product.has_box || false;
    const unitsPerBox = product.units_per_box || 1;
    
    if (noteType === 'supplier') {
      // Para proveedores (entrada): usar coste de compra
      if (hasBox && isBox) {
        // Precio de la caja completa
        return product.cost || 0;
      } else if (hasBox && !isBox) {
        // Precio por unidad = coste de caja / unidades por caja
        return (product.cost || 0) / unitsPerBox;
      } else {
        // Producto sin caja
        return product.cost || 0;
      }
    } else {
      // Para eventos (salida): usar coste medio
      const baseCost = product.average_cost || product.cost || 0;
      if (hasBox && isBox) {
        // Precio de la caja completa (coste medio * unidades por caja)
        return baseCost * unitsPerBox;
      } else {
        // Precio por unidad
        return baseCost;
      }
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Recalculate price when product or is_box changes
    if (field === 'product_id' || field === 'is_box') {
      const productId = field === 'product_id' ? value : newItems[index].product_id;
      const isBox = field === 'is_box' ? value : newItems[index].is_box;
      const product = products.find(p => p.id === productId);
      
      if (product) {
        newItems[index].price = calculateItemPrice(product, isBox, type);
      }
    }
    
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validations
      if (items.length === 0) {
        toast.error('Debe agregar al menos un producto');
        return;
      }

      if (type === 'supplier' && !supplierId) {
        toast.error('Debe seleccionar un proveedor');
        return;
      }

      if (type === 'customer' && !customerId) {
        toast.error('Debe seleccionar un evento');
        return;
      }
      
      if (type === 'customer' && !eventId) {
        toast.error('Debe seleccionar un evento para vincular el albarán');
        return;
      }

      // Check all items are valid
      for (let item of items) {
        if (!item.product_id) {
          toast.error('Todos los productos deben estar seleccionados');
          return;
        }
        if (item.quantity <= 0) {
          toast.error('Las cantidades deben ser mayores a 0');
          return;
        }
        if (item.price < 0) {
          toast.error('Los precios no pueden ser negativos');
          return;
        }
      }

      const data = {
        type,
        date: date + 'T00:00:00Z',
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: parseInt(item.quantity),
          price: parseFloat(item.price),
          is_box: item.is_box || false
        })),
        supplier_id: type === 'supplier' ? supplierId : null,
        customer_id: type === 'customer' ? customerId : null,
        event_id: type === 'customer' ? eventId : null,  // Vincular con evento
        notes
      };

      await api.createDeliveryNote(data);
      toast.success('Albarán creado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchDeliveryNotes();
    } catch (error) {
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
           ? error.response.data.detail 
           : 'Error al crear albarán')
        : 'Error al crear albarán';
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setType('supplier');
    setDate(new Date().toISOString().split('T')[0]);
    setSupplierId('');
    setCustomerId('');
    setEventId('');
    setNotes('');
    setItems([]);
  };

  const viewDetail = async (noteId) => {
    try {
      const response = await api.getDeliveryNote(noteId);
      setSelectedNote(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalle del albarán');
    }
  };

  // Generar nombre automático para el archivo
  const generateAutoFilename = (note) => {
    const typeMap = {
      'supplier': 'ENTRADA_PROV',
      'customer': 'SALIDA_EVENTO',
      'return': 'DEVOLUCION',
      'warehouse_entry': 'ENTRADA_ALM',
      'warehouse_exit': 'SALIDA_ALM'
    };
    const typePrefix = typeMap[note.type] || 'ALBARAN';
    const entityName = getEntityName(note).replace(/\s+/g, '_').toUpperCase().substring(0, 15);
    const noteNum = note.number.replace('ALB-', '');
    return `${typePrefix}_${entityName}_${noteNum}`;
  };

  // Abrir dialog para pedir nombre antes de descargar
  const openDownloadDialog = (note, type) => {
    setNoteToDownload(note);
    setDownloadType(type);
    // Usar el nombre personalizado si existe, o generar uno automático
    setCustomFilename(note.custom_filename || generateAutoFilename(note));
    setFilenameDialogOpen(true);
  };

  // Procesar la descarga con el nombre personalizado
  const handleDownload = async () => {
    if (!noteToDownload || !customFilename.trim()) {
      toast.error('Debes indicar un nombre para el documento');
      return;
    }

    setDownloading(true);
    try {
      // Primero guardar el nombre personalizado en la base de datos
      await api.updateDeliveryNoteFilename(noteToDownload.id, customFilename.trim());
      
      // Luego descargar el archivo
      if (downloadType === 'pdf') {
        const response = await api.getDeliveryNotePDF(noteToDownload.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${customFilename.trim()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF descargado');
      } else {
        const response = await api.getDeliveryNoteExcel(noteToDownload.id);
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${customFilename.trim()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Excel descargado');
      }
      
      // Actualizar la lista de albaranes para mostrar el nuevo nombre
      fetchDeliveryNotes();
      setFilenameDialogOpen(false);
      setNoteToDownload(null);
      setCustomFilename('');
    } catch (error) {
      toast.error('Error al descargar el documento');
    } finally {
      setDownloading(false);
    }
  };

  const confirmDelete = (note) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!noteToDelete) return;
    
    setDeleting(true);
    try {
      const response = await api.deleteDeliveryNote(noteToDelete.id);
      toast.success(response.data.message || 'Albarán eliminado correctamente');
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
      fetchDeliveryNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el albarán');
    } finally {
      setDeleting(false);
    }
  };

  const getEntityName = (note) => {
    if (note.type === 'supplier') {
      const supplier = suppliers.find(s => s.id === note.supplier_id);
      return supplier ? supplier.name : 'N/A';
    } else if (note.type === 'customer' || note.type === 'return') {
      // Para customer y return, ambos usan customer_id (evento)
      const customer = customers.find(c => c.id === note.customer_id);
      return customer ? customer.name : 'N/A';
    } else {
      // warehouse_entry y warehouse_exit no tienen entidad asociada
      return 'Almacén General';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'supplier': return 'Entrada Prov.';
      case 'customer': return 'Salida Evento';
      case 'return': return 'Devolución';
      case 'warehouse_entry': return 'Entrada Alm.';
      case 'warehouse_exit': return 'Salida Alm.';
      default: return type;
    }
  };

  const getTypeBadgeStyle = (type) => {
    switch (type) {
      case 'supplier': return 'bg-green-100 text-green-800';
      case 'customer': return 'bg-blue-100 text-blue-800';
      case 'return': return 'bg-amber-100 text-amber-800';
      case 'warehouse_entry': return 'bg-cyan-100 text-cyan-800';
      case 'warehouse_exit': return 'bg-purple-100 text-purple-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Desconocido';
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  // Filtered delivery notes
  const filteredNotes = useMemo(() => {
    return deliveryNotes.filter(note => {
      // Filter by type
      if (filterType !== 'all' && note.type !== filterType) return false;
      
      // Filter by date range
      if (filterDateFrom) {
        const noteDate = new Date(note.date).toISOString().split('T')[0];
        if (noteDate < filterDateFrom) return false;
      }
      if (filterDateTo) {
        const noteDate = new Date(note.date).toISOString().split('T')[0];
        if (noteDate > filterDateTo) return false;
      }
      
      // Filter by entity (event/supplier)
      if (filterEntity) {
        const entityName = getEntityName(note).toLowerCase();
        if (!entityName.includes(filterEntity.toLowerCase())) return false;
      }
      
      return true;
    });
  }, [deliveryNotes, filterType, filterDateFrom, filterDateTo, filterEntity, suppliers, customers]);

  const clearFilters = () => {
    setFilterType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterEntity('');
  };

  const hasActiveFilters = filterType !== 'all' || filterDateFrom || filterDateTo || filterEntity;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">Albaranes</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-2">Gestiona tus albaranes de entrada y salida</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={showFilters ? "primary" : "secondary"} 
            size="default"
            onClick={() => setShowFilters(!showFilters)}
            className="relative"
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-5 w-5 mr-2" />
            Filtros
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
            )}
          </Button>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button variant="primary" size="default">
              <Plus className="h-5 w-5 mr-2" />
              Nuevo Albarán
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuevo Albarán</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Albarán *</Label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#111827',
                      appearance: 'auto'
                    }}
                  >
                    <option value="supplier" style={{ backgroundColor: '#ffffff', color: '#111827' }}>
                      Proveedor (Entrada)
                    </option>
                    <option value="customer" style={{ backgroundColor: '#ffffff', color: '#111827' }}>
                      Evento (Salida)
                    </option>
                    <option value="return" style={{ backgroundColor: '#ffffff', color: '#111827' }}>
                      Evento (Devolución)
                    </option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date">Fecha *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                {type === 'supplier' ? (
                  <div className="space-y-2">
                    <Label>Proveedor *</Label>
                    <select
                      value={supplierId}
                      onChange={(e) => setSupplierId(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      style={{ backgroundColor: '#ffffff', color: '#111827' }}
                    >
                      <option value="">Selecciona un proveedor</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id} style={{ backgroundColor: '#ffffff', color: '#111827' }}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Evento *</Label>
                      <select
                        value={customerId}
                        onChange={(e) => {
                          setCustomerId(e.target.value);
                          setEventId(e.target.value); // Auto-vincular el evento
                        }}
                        className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        style={{ backgroundColor: '#ffffff', color: '#111827' }}
                      >
                        <option value="">Selecciona un evento</option>
                        {customers.map(customer => (
                          <option key={customer.id} value={customer.id} style={{ backgroundColor: '#ffffff', color: '#111827' }}>
                            {customer.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500">
                        {type === 'return' 
                          ? 'Este albarán de devolución registrará la entrada de productos del evento seleccionado'
                          : 'Este albarán de salida se vinculará automáticamente con el evento seleccionado'
                        }
                      </p>
                    </div>
                  </>
                )}

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="notes">Observaciones</Label>
                  <Input
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notas adicionales..."
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="text-lg font-semibold">Productos</Label>
                  <Button type="button" variant="secondary" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Añadir Producto
                  </Button>
                </div>

                {items.length === 0 ? (
                  <div 
                    className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg"
                    style={{ borderColor: '#cbd5e1' }}
                  >
                    No hay productos agregados. Haz clic en "Añadir Producto" para comenzar.
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden" style={{ borderColor: '#cbd5e1' }}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Producto</TableHead>
                          <TableHead className="w-28">Tipo</TableHead>
                          <TableHead className="w-24">Cantidad</TableHead>
                          <TableHead className="w-32">
                            {type === 'customer' ? 'Coste Medio' : 'Precio/Ud'}
                          </TableHead>
                          <TableHead className="w-28">Total</TableHead>
                          <TableHead className="w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, index) => {
                          const selectedProduct = products.find(p => p.id === item.product_id);
                          const hasBox = selectedProduct?.has_box || false;
                          
                          // Mostrar stock según tipo de producto
                          const getStockDisplay = (product) => {
                            if (!product) return '';
                            if (product.has_box) {
                              return `📦${product.box_stock || 0} + 🔢${product.unit_stock || 0}`;
                            }
                            return `Stock: ${product.stock}`;
                          };
                          
                          return (
                          <TableRow key={index}>
                            <TableCell>
                              <select
                                value={item.product_id}
                                onChange={(e) => {
                                  updateItem(index, 'product_id', e.target.value);
                                  // Reset is_box cuando cambia el producto
                                  updateItem(index, 'is_box', false);
                                }}
                                className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                style={{ backgroundColor: '#ffffff', color: '#111827' }}
                              >
                                <option value="">Selecciona producto</option>
                                {products.map(product => (
                                  <option key={product.id} value={product.id} style={{ backgroundColor: '#ffffff', color: '#111827' }}>
                                    {product.name} ({getStockDisplay(product)})
                                  </option>
                                ))}
                              </select>
                            </TableCell>
                            <TableCell>
                              {hasBox ? (
                                <select
                                  value={item.is_box ? 'box' : 'unit'}
                                  onChange={(e) => updateItem(index, 'is_box', e.target.value === 'box')}
                                  className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  style={{ backgroundColor: '#ffffff', color: '#111827' }}
                                >
                                  <option value="unit">🔢 Unidades</option>
                                  <option value="box">📦 Cajas</option>
                                </select>
                              ) : (
                                <span className="text-sm text-slate-500">Unidades</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                inputMode="numeric"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                onFocus={handleNumericInputFocus}
                                onMouseUp={handleNumericInputMouseUp}
                                onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={item.price}
                                onChange={(e) => updateItem(index, 'price', e.target.value)}
                                onFocus={handleNumericInputFocus}
                                onMouseUp={handleNumericInputMouseUp}
                                onKeyDown={(e) => {if (e.key === '-' || e.key === 'e') e.preventDefault();}}
                                readOnly={type === 'customer'}
                                disabled={type === 'customer'}
                                className={type === 'customer' ? 'bg-slate-100 cursor-not-allowed' : ''}
                                title={type === 'customer' ? 'Se usa automáticamente el coste medio del producto' : ''}
                              />
                            </TableCell>
                            <TableCell className="font-semibold">
                              €{(item.quantity * item.price).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="danger"
                                size="icon-sm"
                                onClick={() => removeItem(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )})}
                        <TableRow>
                          <TableCell colSpan={4} className="text-right font-semibold">
                            TOTAL:
                          </TableCell>
                          <TableCell className="font-bold text-lg">
                            €{calculateTotal(items).toFixed(2)}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button 
                  type="button" 
                  variant="secondary" 
                  size="default"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  variant="success"
                  size="default"
                >
                  Crear Albarán
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Tipo</Label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ backgroundColor: '#ffffff', color: '#111827' }}
                  data-testid="filter-type"
                >
                  <option value="all">Todos</option>
                  <option value="supplier">Entrada (Proveedor)</option>
                  <option value="customer">Salida (Evento)</option>
                  <option value="return">Devolución (Evento)</option>
                  <option value="warehouse_entry">Entrada (Almacén)</option>
                  <option value="warehouse_exit">Salida (Almacén)</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Desde</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  data-testid="filter-date-from"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Hasta</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  data-testid="filter-date-to"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Evento/Proveedor</Label>
                <Input
                  type="text"
                  placeholder="Buscar..."
                  value={filterEntity}
                  onChange={(e) => setFilterEntity(e.target.value)}
                  data-testid="filter-entity"
                />
              </div>
              
              <div>
                <Button 
                  variant="secondary" 
                  size="default"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  className="w-full"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpiar
                </Button>
              </div>
            </div>
            
            {hasActiveFilters && (
              <p className="text-sm text-slate-500 mt-3">
                Mostrando {filteredNotes.length} de {deliveryNotes.length} albaranes
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Detalle del Albarán</DialogTitle>
              {selectedNote && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => downloadPDF(selectedNote.id, selectedNote.number)}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Descargar PDF
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedNote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold">Número:</span> {selectedNote.number}
                </div>
                <div>
                  <span className="font-semibold">Fecha:</span> {new Date(selectedNote.date).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-semibold">Tipo:</span> {selectedNote.type === 'supplier' ? 'Proveedor (Entrada)' : 'Evento (Salida)'}
                </div>
                <div>
                  <span className="font-semibold">{selectedNote.type === 'supplier' ? 'Proveedor' : 'Evento'}:</span> {getEntityName(selectedNote)}
                </div>
                {selectedNote.notes && (
                  <div className="col-span-2">
                    <span className="font-semibold">Observaciones:</span> {selectedNote.notes}
                  </div>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Precio/Unidad</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedNote.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{getProductName(item.product_id)}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">€{item.price.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-semibold">€{(item.quantity * item.price).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-semibold">TOTAL:</TableCell>
                      <TableCell className="text-right font-bold text-lg">
                        €{calculateTotal(selectedNote.items).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* List */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <CardHeader>
          <CardTitle>Lista de Albaranes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase tracking-wider text-xs">Número</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Nombre Doc.</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Fecha</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Tipo</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Evento/Proveedor</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Productos</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Total</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Origen</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotes.map((note) => (
                  <TableRow key={note.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                    <TableCell className="font-medium">{note.number}</TableCell>
                    <TableCell>
                      {note.custom_filename ? (
                        <span className="text-sm text-blue-600 font-medium" title={note.custom_filename}>
                          {note.custom_filename.length > 20 
                            ? note.custom_filename.substring(0, 20) + '...' 
                            : note.custom_filename}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Sin nombre</span>
                      )}
                    </TableCell>
                    <TableCell>{new Date(note.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeBadgeStyle(note.type)}`}>
                        {getTypeLabel(note.type)}
                      </span>
                    </TableCell>
                    <TableCell>{getEntityName(note)}</TableCell>
                    <TableCell>{note.items.length}</TableCell>
                    <TableCell className="font-semibold">€{calculateTotal(note.items).toFixed(2)}</TableCell>
                    <TableCell>
                      {note.source === 'scanner' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <Scan className="h-3 w-3 mr-1" />
                          Escáner
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">Manual</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="secondary" 
                          size="icon-sm" 
                          onClick={() => viewDetail(note.id)}
                          className="hover:bg-blue-100 hover:text-blue-700"
                          title="Ver detalle"
                          data-testid={`view-note-${note.number}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="icon-sm" 
                          onClick={() => openDownloadDialog(note, 'pdf')}
                          className="hover:bg-red-100 hover:text-red-700"
                          title="Descargar PDF"
                          data-testid={`download-pdf-${note.number}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="icon-sm" 
                          onClick={() => openDownloadDialog(note, 'excel')}
                          className="hover:bg-green-100 hover:text-green-700"
                          title="Descargar Excel"
                          data-testid={`download-excel-${note.number}`}
                        >
                          <FileSpreadsheet className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="icon-sm" 
                          onClick={() => confirmDelete(note)}
                          className="hover:bg-red-100 hover:text-red-700"
                          title="Eliminar albarán"
                          data-testid={`delete-note-${note.number}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {filteredNotes.length === 0 && deliveryNotes.length > 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No se encontraron albaranes con los filtros aplicados
              <Button 
                variant="link" 
                onClick={clearFilters}
                className="ml-2"
              >
                Limpiar filtros
              </Button>
            </div>
          )}
          {deliveryNotes.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No hay albaranes registrados
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Albarán</DialogTitle>
          </DialogHeader>
          {noteToDelete && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium text-sm">
                  ⚠️ Esta acción revertirá los movimientos de stock:
                </p>
                <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                  {['supplier', 'return', 'warehouse_entry'].includes(noteToDelete.type) ? (
                    <li>Las unidades se <strong>restarán</strong> del stock actual</li>
                  ) : (
                    <li>Las unidades se <strong>devolverán</strong> al stock actual</li>
                  )}
                </ul>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Albarán a eliminar:</p>
                <p className="font-semibold">{noteToDelete.number}</p>
                <p className="text-sm text-slate-500">
                  {getTypeLabel(noteToDelete.type)} - {getEntityName(noteToDelete)}
                </p>
                <p className="text-sm text-slate-500">
                  {noteToDelete.items.length} producto(s)
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={deleting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deleting ? 'Eliminando...' : 'Eliminar Albarán'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Filename Dialog for Download */}
      <Dialog open={filenameDialogOpen} onOpenChange={setFilenameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {downloadType === 'pdf' ? '📄 Descargar PDF' : '📊 Descargar Excel'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                Indica el nombre con el que se guardará este documento. 
                El nombre se guardará para futuras descargas.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="filename">Nombre del documento</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="filename"
                  value={customFilename}
                  onChange={(e) => setCustomFilename(e.target.value)}
                  placeholder="Ej: ALBARAN_FERIA_MAYO_2025"
                  className="flex-1"
                  data-testid="filename-input"
                />
                <span className="text-sm text-slate-500">
                  .{downloadType === 'pdf' ? 'pdf' : 'xlsx'}
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Se eliminarán caracteres especiales automáticamente
              </p>
            </div>
            
            {noteToDownload && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium">{noteToDownload.number}</p>
                <p className="text-slate-500">
                  {getTypeLabel(noteToDownload.type)} - {getEntityName(noteToDownload)}
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setFilenameDialogOpen(false);
                  setNoteToDownload(null);
                  setCustomFilename('');
                }}
                disabled={downloading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDownload}
                disabled={downloading || !customFilename.trim()}
                className={downloadType === 'pdf' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'}
              >
                {downloading ? 'Descargando...' : `Descargar ${downloadType.toUpperCase()}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
